import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { PREVIEW, PREVIEW_SIGNED_OUT } from "./preview";
import { supabase } from "./supabase";

const LAST_USER = "ops-portal:last-user";

/** Minimal view of the signed-in user the shell needs. */
export interface SignedInUser {
  id: string;
  email: string;
}

/**
 * Current Supabase session: `undefined` while loading, `null` when signed out.
 * In DEV preview mode a stand-in session is returned without touching Supabase.
 */
export function useSession(): SignedInUser | null | undefined {
  const [user, setUser] = useState<SignedInUser | null | undefined>(undefined);

  useEffect(() => {
    if (PREVIEW) {
      if (PREVIEW_SIGNED_OUT) setUser(null);
      else void import("../dev/fixtures").then(({ profile }) => setUser({ id: profile.id, email: profile.email ?? "" }));
      return;
    }
    const toUser = (s: Session | null) => (s ? { id: s.user.id, email: s.user.email ?? "" } : null);
    // With no signal an expired session can't be refreshed: stay signed in as the last user, so the job packs
    // saved on the phone still open. The session refreshes once the phone is back online.
    const remember = (u: SignedInUser | null) => {
      try {
        if (u) window.localStorage.setItem(LAST_USER, JSON.stringify(u));
      } catch {
        /* storage blocked */
      }
      return u;
    };
    const lastUser = (): SignedInUser | null => {
      try {
        return navigator.onLine === false ? (JSON.parse(window.localStorage.getItem(LAST_USER) ?? "null") as SignedInUser | null) : null;
      } catch {
        return null;
      }
    };
    supabase.auth.getSession().then(({ data }) => setUser(remember(toUser(data.session)) ?? lastUser()), () => setUser(lastUser()));
    const { data } = supabase.auth.onAuthStateChange((e, s) => setUser(remember(toUser(s)) ?? (e === "SIGNED_OUT" ? null : lastUser())));
    return () => data.subscription.unsubscribe();
  }, []);

  return user;
}

/**
 * Why a sign-in link was refused, so each sign-in page can say it in its own words. Sign-up is off, so Supabase
 * refuses an address with no login ("Signups not allowed for otp", code otp_disabled). An invited login whose
 * invitation was never accepted has an unconfirmed email, which Supabase handles as a new sign-up and refuses with
 * code signup_disabled ("Signups not allowed for this instance").
 */
export type SignInProblem = "not_invited" | "invite_not_accepted" | "not_invited_or_pending" | "rate_limited" | "other";

export function signInProblem(error: { message: string; code?: string }): SignInProblem {
  if (error.code === "otp_disabled" || /for otp/i.test(error.message)) return "not_invited";
  if (error.code === "signup_disabled" || /for this instance/i.test(error.message)) return "invite_not_accepted";
  if (/signups? not allowed|user not found/i.test(error.message)) return "not_invited_or_pending";
  if (error.code === "over_email_send_rate_limit" || /rate limit|too many/i.test(error.message)) return "rate_limited";
  return "other";
}

/** Magic link sign-in. Accounts are invited by a director; sign-up is disabled. */
export async function sendMagicLink(email: string): Promise<{ error: { message: string; problem: SignInProblem } | null }> {
  if (PREVIEW) {
    await new Promise((r) => setTimeout(r, 600));
    // Preview: "error..." has no login, "invited..." has an invitation that was never accepted.
    const e = email.startsWith("error")
      ? { message: "Signups not allowed for otp", code: "otp_disabled" }
      : email.startsWith("invited")
        ? { message: "Signups not allowed for this instance", code: "signup_disabled" }
        : null;
    return { error: e && { message: e.message, problem: signInProblem(e) } };
  }
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
  });
  return { error: error ? { message: error.message, problem: signInProblem(error) } : null };
}

export async function signOut(): Promise<void> {
  if (PREVIEW) {
    window.location.search = "?preview=login";
    return;
  }
  try {
    window.localStorage.removeItem(LAST_USER);
  } catch {
    /* storage blocked */
  }
  await supabase.auth.signOut();
}
